import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  FiArrowLeft,
  FiCalendar,
  FiChevronRight,
  FiEdit2,
  FiExternalLink,
  FiHome,
  FiMapPin,
  FiMaximize2,
  FiNavigation,
  FiEye,
  FiPackage,
  FiRefreshCw,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";
import {
  MdHeight,
  MdOutlineWarehouse,
  MdStraighten,
  MdSwapHoriz,
} from "react-icons/md";
import { logisticTypes, RoutesPaths } from "../Router/constant";
import {
  assertApiSuccess,
  deleteWarehouse,
  extractWarehouse,
  extractWarehouseOrders,
  getApiMessage,
  getWarehouseById,
  setWarehouseStatus,
} from "../Services/warehouseService";
import Skeleton from "../Components/SkeletonLoader";
import AdminOnly from "../Components/AdminOnly";
import { warehouseImageUrl, WarehouseActionModal } from "./Warehouse";

// Same key and loader options as the order form (OrderForm.js) so the Maps script is shared.
const GOOGLE_MAPS_API_KEY = "AIzaSyBk6NKU8aUcbaQgqNJlGgh9ARAG6q5PkrA";
const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "280px",
  borderRadius: "12px",
};

const prettyLogistics = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "--";

function WarehouseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [orders, setOrders] = useState([]);
  // type: "delete" | "status"
  const [confirmation, setConfirmation] = useState({
    show: false,
    type: "",
    warehouse: null,
  });
  const [working, setWorking] = useState(false);
  const closeConfirmation = () =>
    !working && setConfirmation({ show: false, type: "", warehouse: null });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await getWarehouseById(id);
      const data = extractWarehouse(response);
      if (!data) throw new Error("Warehouse not found");
      setWarehouse(data);
      setOrders(extractWarehouseOrders(response));
      setActiveImage(0);
    } catch (error) {
      console.error("Load warehouse error:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleConfirm = async () => {
    const { type } = confirmation;
    if (working || !warehouse) return;

    setWorking(true);
    try {
      if (type === "delete") {
        const response = assertApiSuccess(
          await deleteWarehouse(id),
          "Failed to delete warehouse",
        );
        toast.success(
          getApiMessage(response, "Warehouse deleted successfully"),
        );
        setConfirmation({ show: false, type: "", warehouse: null });
        navigate(RoutesPaths.WAREHOUSE);
        return;
      }

      const nextActive = warehouse.isActive === false;
      const response = assertApiSuccess(
        await setWarehouseStatus(id, nextActive),
        "Failed to update warehouse status",
      );
      toast.success(
        getApiMessage(
          response,
          nextActive ? "Warehouse activated" : "Warehouse deactivated",
        ),
      );
      setWarehouse((prev) => ({ ...prev, isActive: nextActive }));
      setConfirmation({ show: false, type: "", warehouse: null });
    } catch (error) {
      console.error("Warehouse action error:", error);
      // e.g. "Warehouse cannot be deleted because an order already uses this warehouse."
      toast.error(
        getApiMessage(
          error,
          type === "delete"
            ? "Failed to delete warehouse"
            : "Failed to update warehouse status",
        ),
      );
    } finally {
      setWorking(false);
    }
  };

  const breadcrumb = (
    <nav className="qd-breadcrumb">
      <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
        <FiHome />
      </NavLink>
      <FiChevronRight />
      <NavLink to={RoutesPaths.WAREHOUSE}>Warehouse</NavLink>
      <FiChevronRight />
      <span className="qd-breadcrumb-current">
        {warehouse?.name || "Details"}
      </span>
    </nav>
  );

  if (loadError) {
    return (
      <div className="container-fluid qd-page">
        {breadcrumb}
        <div className="qd-card qd-panel">
          <div className="qu-empty">
            <span className="qu-empty-art">
              <MdOutlineWarehouse />
            </span>
            <h6>Unable to load this warehouse</h6>
            <p>It may have been removed, or there was a problem loading it.</p>
            <div className="qd-bookings-tools">
              <NavLink
                to={RoutesPaths.WAREHOUSE}
                className="qd-btn qd-btn-outline"
              >
                <FiArrowLeft /> Back to warehouses
              </NavLink>
              <button
                type="button"
                className="qd-btn qd-btn-primary"
                onClick={load}
              >
                <FiRefreshCw /> Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const active = warehouse?.isActive !== false;
  const images = Array.isArray(warehouse?.uploadImages)
    ? warehouse.uploadImages
    : [];
  const { length, width, height } = warehouse?.dimensions || {};
  const hasDims = [length, width, height].every(
    (v) => v !== undefined && v !== null && v !== "",
  );
  const lat = Number(warehouse?.latitude);
  const lng = Number(warehouse?.longitude);
  const hasPosition =
    warehouse && !isNaN(lat) && !isNaN(lng) && warehouse.latitude !== undefined;
  const mapsLink = hasPosition
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : null;

  return (
    <div className="container-fluid qd-page">
      {breadcrumb}

      {/* Hero */}
      <div className="qd-card qw-hero">
        <div className="qw-hero-main">
          <span className="qh-order-icon">
            <MdOutlineWarehouse />
          </span>
          <div>
            {loading ? (
              <>
                <Skeleton width={260} height={26} />
                <Skeleton width={180} height={16} />
              </>
            ) : (
              <>
                <div className="qw-hero-title">
                  <h1>{warehouse.name || "Untitled warehouse"}</h1>
                  <span
                    className={`qc-status ${active ? "qp-status-accepted" : "qp-status-rejected"}`}
                  >
                    <span className="qu-dot" />
                    {active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="qw-hero-location">
                  <FiMapPin /> {warehouse.location || "--"}
                </p>
                <div className="qw-hero-meta">
                  <span className="qw-meta-chip">
                    <FiCalendar aria-hidden="true" />
                    <span>Created</span>
                    <strong>{formatDate(warehouse.createdAt)}</strong>
                  </span>
                  <span className="qw-meta-chip">
                    <FiRefreshCw aria-hidden="true" />
                    <span>Last updated</span>
                    <strong>{formatDate(warehouse.updatedAt)}</strong>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        {!loading && (
          <div className="qd-bookings-tools">
            <button
              type="button"
              className={`qd-btn qd-btn-outline qw-status-btn ${active ? "is-on" : "is-off"}`}
              onClick={() =>
                setConfirmation({ show: true, type: "status", warehouse })
              }
            >
              {active ? <FiToggleRight /> : <FiToggleLeft />}
              {active ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              className="qd-btn qp-delete-btn"
              onClick={() =>
                setConfirmation({ show: true, type: "delete", warehouse })
              }
              disabled={orders.length > 0}
              title={
                orders.length > 0
                  ? "Can't delete: this warehouse is used by an order. Deactivate it instead."
                  : "Delete warehouse"
              }
            >
              <FiTrash2 /> Delete
            </button>
            <button
              type="button"
              className="qd-btn qd-btn-primary"
              onClick={() => navigate(`${RoutesPaths.EDITWAREHOUSE}/${id}`)}
            >
              <FiEdit2 /> Edit warehouse
            </button>
          </div>
        )}
      </div>

      <div className="qh-layout">
        <div className="qh-main">
          {/* Photos */}
          <section className="qd-card qh-card">
            <div className="qh-card-head">
              <div className="qd-panel-title">
                <h6>Warehouse photos</h6>
                {!loading && (
                  <span className="qd-muted">{images.length} uploaded</span>
                )}
              </div>
            </div>
            {loading ? (
              <div className="qd-skel" style={{ height: 300 }} />
            ) : images.length === 0 ? (
              <div className="qw-no-photos">
                <MdOutlineWarehouse />
                <span>No photos uploaded</span>
              </div>
            ) : (
              <>
                <div className="qw-gallery-main">
                  <img
                    src={warehouseImageUrl(images[activeImage])}
                    alt={`${warehouse.name} ${activeImage + 1}`}
                  />
                </div>
                {images.length > 1 && (
                  <div className="qw-gallery-thumbs">
                    {images.map((fileName, index) => (
                      <button
                        type="button"
                        key={fileName}
                        className={index === activeImage ? "active" : ""}
                        onClick={() => setActiveImage(index)}
                        aria-label={`Show photo ${index + 1}`}
                      >
                        <img src={warehouseImageUrl(fileName)} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          {/* Description */}
          <section className="qd-card qh-card">
            <div className="qh-card-head">
              <div className="qd-panel-title">
                <h6>Warehouse description</h6>
              </div>
            </div>
            {loading ? (
              <Skeleton count={3} />
            ) : (
              <p className="qw-description">
                {warehouse.description || "No description added."}
              </p>
            )}
          </section>

          {/* Orders using this warehouse */}
          <section className="qd-card qd-panel qw-orders">
            <div className="qd-panel-header">
              <div className="qd-panel-title">
                <span className="qd-panel-icon">
                  <FiPackage />
                </span>
                <div>
                  <h6>
                    Orders{" "}
                    {!loading && (
                      <span className="qw-orders-count">{orders.length}</span>
                    )}
                  </h6>
                  <p className="qd-panel-sub">
                    Bookings that use this warehouse for storage.
                  </p>
                </div>
              </div>
            </div>

            <div className="qd-table-wrap">
              <table className="qd-table qw-orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Logistics type</th>
                    <th>Space requested</th>
                    <th>Days</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <tr key={i}>
                        <td>
                          <Skeleton width={150} />
                        </td>
                        <td>
                          <Skeleton width={120} />
                        </td>
                        <td>
                          <Skeleton width={90} />
                        </td>
                        <td>
                          <Skeleton width={100} />
                        </td>
                        <td>
                          <Skeleton width={40} />
                        </td>
                        <td>
                          <Skeleton width={80} height={24} />
                        </td>
                        <td>
                          <Skeleton width={90} />
                        </td>
                        <td>
                          <Skeleton width={70} height={32} />
                        </td>
                      </tr>
                    ))
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="qd-empty">
                        No orders use this warehouse yet.
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => {
                      const storage = order?.warehouse || {};
                      const dims = storage.dimensions || {};
                      const hasDims = [
                        dims.length,
                        dims.width,
                        dims.height,
                      ].every((v) => v !== undefined && v !== null && v !== "");
                      const days = Number(storage.numberOfDays);
                      const statusKey = String(order?.status || "")
                        .toLowerCase()
                        .replace(/\s+/g, "");
                      const logistics = logisticTypes[order?.logisticsType];

                      return (
                        <tr key={order?._id || index}>
                          <td className="qd-mono">
                            {order?.orderId ? `#${order.orderId}` : "--"}
                          </td>
                          <td>
                            {order?.name ? (
                              <span className="qd-customer">
                                <span className="qd-avatar">
                                  {order.name.trim().charAt(0).toUpperCase()}
                                </span>
                                <strong>{order.name}</strong>
                              </span>
                            ) : (
                              "--"
                            )}
                          </td>
                          <td>
                            {logistics ? prettyLogistics(logistics) : "--"}
                          </td>
                          <td className="qd-mono">
                            {hasDims
                              ? `${dims.length} × ${dims.width} × ${dims.height} m`
                              : "--"}
                          </td>
                          <td>{days > 0 ? days : "--"}</td>
                          <td>
                            {order?.status ? (
                              <span
                                className={`qd-status qd-status-${statusKey}`}
                              >
                                {String(order.status).replace(/\b\w/g, (c) =>
                                  c.toUpperCase(),
                                )}
                              </span>
                            ) : (
                              "--"
                            )}
                          </td>
                          <td>
                            {order?.createdAt ? (
                              <span className="qd-date">
                                <FiCalendar />
                                {new Date(order.createdAt).toLocaleDateString(
                                  "en-GB",
                                )}
                              </span>
                            ) : (
                              "--"
                            )}
                          </td>
                          <td>
                            {order?._id ? (
                              <NavLink
                                to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${order._id}`}
                                className="qd-btn qd-btn-outline qd-btn-sm"
                              >
                                <FiEye /> View
                              </NavLink>
                            ) : (
                              "--"
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="qh-side">
          {/* Dimensions */}
          <section className="qd-card qh-card">
            <div className="qh-card-head">
              <div className="qd-panel-title">
                <span className="qd-panel-icon">
                  <FiMaximize2 />
                </span>
                <h6>Dimensions</h6>
              </div>
            </div>
            <div className="qw-dims">
              {[
                { label: "Length", value: length, Icon: MdStraighten },
                { label: "Width", value: width, Icon: MdSwapHoriz },
                { label: "Height", value: height, Icon: MdHeight },
              ].map(({ label, value, Icon }) => (
                <div className="qw-dim" key={label}>
                  <Icon />
                  <span>{label}</span>
                  <strong>
                    {loading ? (
                      <Skeleton width={40} />
                    ) : value !== undefined && value !== "" ? (
                      `${value} m`
                    ) : (
                      "--"
                    )}
                  </strong>
                </div>
              ))}
            </div>
            {!loading && hasDims && (
              <p className="qw-dim-total">
                Floor area{" "}
                <strong>
                  {(Number(length) * Number(width)).toLocaleString()} m²
                </strong>{" "}
                · Volume{" "}
                <strong>
                  {(
                    Number(length) *
                    Number(width) *
                    Number(height)
                  ).toLocaleString()}{" "}
                  m³
                </strong>
              </p>
            )}
          </section>

          {/* Location */}
          <section className="qd-card qh-card">
            <div className="qh-card-head">
              <div className="qd-panel-title">
                <span className="qd-panel-icon">
                  <FiMapPin />
                </span>
                <h6>Location</h6>
              </div>
              {mapsLink && (
                <a
                  className="qd-btn qd-btn-outline qd-btn-sm"
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink /> Open in Maps
                </a>
              )}
            </div>
            <div className="qw-map qw-map-detail">
              {loading ? (
                <div className="qd-skel" style={{ height: 280 }} />
              ) : hasPosition && isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{ lat, lng }}
                  zoom={14}
                >
                  <Marker position={{ lat, lng }} />
                </GoogleMap>
              ) : (
                <div className="qo-map-loading" style={{ height: 280 }}>
                  {hasPosition ? "Loading map..." : "No map position saved"}
                </div>
              )}
            </div>
            {!loading && (
              <div className="qw-coords-read">
                <span>
                  <FiNavigation />{" "}
                  {hasPosition ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "--"}
                </span>
              </div>
            )}
          </section>

          {/* Record */}
          {/* <section className="qd-card qh-card">
            <div className="qw-record">
              <span>
                <FiCalendar /> Created
              </span>
              <strong>{loading ? <Skeleton width={120} /> : formatDate(warehouse.createdAt)}</strong>
              <span>
                <FiCalendar /> Last updated
              </span>
              <strong>{loading ? <Skeleton width={120} /> : formatDate(warehouse.updatedAt)}</strong>
            </div>
          </section> */}
        </div>
      </div>

      <WarehouseActionModal
        confirmation={confirmation}
        working={working}
        onConfirm={handleConfirm}
        onCancel={closeConfirmation}
      />
    </div>
  );
}

export default function WarehouseDetail() {
  return (
    <AdminOnly>
      <WarehouseDetailPage />
    </AdminOnly>
  );
}
